// Members and invitations — how a person becomes staff, and stops being (spec/07 §6.1–§6.5, F-IAM-08).
//
// An owner or manager invites someone **by phone or by email**, naming one organisation role and
// the venues they may work at. The invitee opens the link, signs in with the account they already
// have — the one with that phone number or that email address, verified — and accepts. There is no
// separate staff account: a cashier who also plays has one login (spec/07 §1).
//
//   GET    /v1/organizations/{o}/members                          members:read
//   GET    /v1/organizations/{o}/members/{memberId}               members:read
//   PUT    /v1/organizations/{o}/members/{memberId}/role          members:roles
//   PUT    /v1/organizations/{o}/members/{memberId}/venues        members:update
//   PUT    /v1/organizations/{o}/members/{memberId}/status        members:remove
//   DELETE /v1/organizations/{o}/members/{memberId}               members:remove
//   GET    /v1/organizations/{o}/roles                            members:read
//   POST   /v1/organizations/{o}/roles                            members:roles   (a custom role)
//   GET    /v1/organizations/{o}/roles/{role}                     members:read
//   PUT    /v1/organizations/{o}/roles/{role}                     members:roles   (a custom role)
//   DELETE /v1/organizations/{o}/roles/{role}                     members:roles   (a custom role)
//   PUT    /v1/organizations/{o}/roles/{role}/limits              members:roles
//   GET    /v1/organizations/{o}/members/{memberId}/limits        members:read    (a member's comp override)
//   PUT    /v1/organizations/{o}/members/{memberId}/limits        members:roles
//   POST   /v1/organizations/{o}/invitations                      members:invite
//   GET    /v1/organizations/{o}/invitations                      members:read
//   DELETE /v1/organizations/{o}/invitations/{invitationId}       members:invite
//   POST   /v1/invitations/accept                                 a session, and the invited contact
//   POST   /v1/invitations/preview                                the link alone: what it is, before signing in
//
// Every change here is audited, one row each (spec/07 §11). Holding the permission is not the whole
// rule: a role is granted, changed or taken away only when it is below the actor's own — its
// permissions a strict subset of theirs — an owner is touched only by an owner, and the last active
// owner can neither leave, be demoted nor be suspended (spec/04 §2's invariant).
//
// **Suspending** a member (F-ORG-13) ends their staff access exactly as removing them does, but keeps
// the membership — its role and its venues — for reinstating. **Role limits** (spec/07 §6.4) are the
// numbers a role's holders may not exceed with the permission they bound: comp minutes per action and
// per shift, the manual discount, the refund that needs no approval, the wallet adjustment.
//
// **Custom roles** (F-IAM-11, spec/07 §6.2–§6.3) are an organisation's own bundles of the permissions
// in `permissions/index.ts` — the statements defined in code — at most twenty of them. Whoever makes
// or changes one may only put in it permissions they hold themselves, the role must stay below their
// own, and because a role reaches its holders at every venue only someone who works at every venue
// makes, changes or deletes one. A change reaches the holders' open consoles as `access.changed`.
//
// Accepting is not under `/v1/organizations/{o}/` because the person accepting is not a member yet,
// and the organisation routes answer a non-member with `404`. The organisation is named by the
// invitation's link, which the server signed.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { InvitationIdSchema, MemberIdSchema, OrganizationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MEMBERS_INVITE, MEMBERS_READ, MEMBERS_REMOVE, MEMBERS_ROLES, MEMBERS_UPDATE } from "../permissions/index.js";
import { NonNegativeMoneySchema } from "../primitives/money.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { PhoneNumberSchema } from "../primitives/phone-number.js";
import { IsoDateTimeSchema, PermissionSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
/** How long an invitation link works (spec/07 §6.5): seven days. */
export const INVITATION_LIFETIME_SECONDS = 7 * 24 * 60 * 60;
/** At most this many venues on one member's scope. An organisation with more gives all venues. */
export const MEMBER_VENUE_SCOPE_MAX = 100;
/** How many custom roles one organisation may have (spec/07 §6.2, F-IAM-11). */
export const CUSTOM_ROLES_MAX = 20;
/** A role name: one of spec/07 §6.2's, or a custom role of the organisation (F-IAM-11). */
export const RoleNameSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,63}$/, 'A role name is lower case letters, digits and underscores')
    .openapi('RoleName', { description: '`owner`, `manager`, `cashier`, … or a custom role of the organisation.' });
/**
 * The venues a member may work at (spec/07 §6.1). **Empty means all of the organisation's venues**,
 * including ones opened later — the same as having no `member_venue_scope` rows.
 */
export const VenueScopeSchema = z
    .array(VenueIdSchema)
    .max(MEMBER_VENUE_SCOPE_MAX)
    .openapi('VenueScope', {
    description: 'Venue ids this member may work at. Empty: every venue of the organisation.',
});
/**
 * `active`, or `suspended`: a suspended member has no staff access at all — every console route
 * answers them as it answers someone who is not on the staff — but keeps their role and venues, which
 * come back unchanged when they are reinstated (F-ORG-13).
 */
export const MEMBER_STATUSES = ['active', 'suspended'];
export const MemberStatusSchema = z.enum(MEMBER_STATUSES).openapi('MemberStatus', {
    description: '`suspended`: no staff access until reinstated; the role and venues are kept.',
});
export const MemberSchema = z
    .object({
    id: MemberIdSchema,
    organizationId: OrganizationIdSchema,
    role: RoleNameSchema,
    venueIds: VenueScopeSchema,
    status: MemberStatusSchema,
    /** When they were suspended; `null` while active. */
    suspendedAt: IsoDateTimeSchema.nullable(),
    /** The person's name, when they have given one. A phone sign-up has none until they do. */
    name: z.string().nullable(),
    /** Masked: enough to tell two apart, never the whole number (spec/20 §6). */
    phoneNumber: z.string().nullable(),
    /** Masked, like the phone number. Never a synthetic `.invalid` address. */
    email: z.string().nullable(),
    /** Whether the person has an authenticator set up (the staff screen shows it, spec/13 §9). */
    secondFactorEnabled: z.boolean(),
    joinedAt: IsoDateTimeSchema,
})
    .openapi('Member', { description: 'A person on the staff of one organisation (spec/07 §6.1).' });
export const MemberListSchema = paginated(MemberSchema, 'MemberList');
export const ChangeMemberRoleSchema = z.object({ role: RoleNameSchema }).openapi('ChangeMemberRole');
export const ChangeMemberVenuesSchema = z.object({ venueIds: VenueScopeSchema }).openapi('ChangeMemberVenues');
export const ChangeMemberStatusSchema = z.object({ status: MemberStatusSchema }).openapi('ChangeMemberStatus');
// ---------------------------------------------------------------------------------------------
// Role limits (spec/07 §6.4)
// ---------------------------------------------------------------------------------------------
/** The most comp minutes a limit can name: a day. */
export const ROLE_LIMIT_MAX_MINUTES = 24 * 60;
/**
 * What each role limit bounds, and where the number it tightens lives (spec/07 §6.4).
 *
 * A limit is read **with** the permission it bounds, by the endpoint that permission gates: the comp
 * (F-SES-16, #264), the manual discount (F-POS-05, #132), the refund (F-POS-08, #133) and the wallet
 * adjustment (F-WAL-07, #298). The comp route enforces both of its limits, per action and per shift, and declares
 * `sessions:comp_unlimited` as its limit lift (`x-clavian-unlimited-by`, F-SES-16); `orders:discount_unlimited` joins
 * the catalogue with the endpoint it lifts.
 *
 * The effective limit is the **lower** of the setting and the role's limit; `null` on the role is
 * "no limit of the role's own" — the setting alone applies. `unlimitedBy` lifts the setting's cap and
 * never the role's: a limit set on a role is a deliberate choice about that role
 * (`effectiveRoleLimit` in `@clavian/domain`).
 */
export const ROLE_LIMITS = {
    compMaxMinutesPerAction: {
        permission: 'sessions:comp',
        unlimitedBy: 'sessions:comp_unlimited',
        setting: 'sessions.compMaxMinutesPerAction',
        unit: 'minutes',
    },
    compMaxMinutesPerShift: {
        permission: 'sessions:comp',
        unlimitedBy: 'sessions:comp_unlimited',
        setting: 'sessions.compMaxMinutesPerShift',
        unit: 'minutes',
    },
    manualDiscountMaxBps: {
        permission: 'orders:discount',
        unlimitedBy: 'orders:discount_unlimited',
        setting: 'pos.manualDiscountMaxBps',
        unit: 'bps',
    },
    refundApprovalAmount: {
        permission: 'orders:refund',
        unlimitedBy: null,
        setting: 'pos.refundApprovalAmount',
        unit: 'money',
    },
    walletAdjustMaxAmount: {
        permission: 'wallet:adjust',
        unlimitedBy: null,
        setting: 'wallet.adjustMaxAmount',
        unit: 'money',
    },
};
export const ROLE_LIMIT_KEYS = Object.keys(ROLE_LIMITS);
const minutesLimit = z.int().min(0).max(ROLE_LIMIT_MAX_MINUTES).nullable();
export const RoleLimitsSchema = z
    .object({
    /**
     * The role's holders comp with **no limit at all** — neither the role's numbers nor the settings'
     * (owner decision 2026-09-30, #709). `false` (the default) is today's rule. When `true`, both comp
     * limits are `null`. Only an owner, or someone whose own comp is unlimited this way, gives it.
     */
    compUnlimited: z.boolean().default(false),
    /** Comp minutes in one grant (`sessions.compMaxMinutesPerAction`). */
    compMaxMinutesPerAction: minutesLimit,
    /** Comp minutes in one shift (`sessions.compMaxMinutesPerShift`). */
    compMaxMinutesPerShift: minutesLimit,
    /** The largest manual discount, in basis points: 1000 is 10% (`pos.manualDiscountMaxBps`). */
    manualDiscountMaxBps: z.int().min(0).max(10_000).nullable(),
    /** The largest refund that needs no second member's approval (`pos.refundApprovalAmount`). */
    refundApprovalAmount: NonNegativeMoneySchema.nullable(),
    /** The largest wallet adjustment (`wallet.adjustMaxAmount`). */
    walletAdjustMaxAmount: NonNegativeMoneySchema.nullable(),
})
    .strict()
    .openapi('RoleLimits', {
    description: 'The numbers a role may not exceed (spec/07 §6.4). `null` is no limit of the role’s own: the setting alone ' +
        'applies. The lower of the two always wins — except that `compUnlimited` is no comp limit at all.',
});
/** A role with no limits of its own. */
export const NO_ROLE_LIMITS = {
    compUnlimited: false,
    compMaxMinutesPerAction: null,
    compMaxMinutesPerShift: null,
    manualDiscountMaxBps: null,
    refundApprovalAmount: null,
    walletAdjustMaxAmount: null,
};
// ---------------------------------------------------------------------------------------------
// A member's own comp limit (spec/07 §6.4, owner decision 2026-09-30, #709)
// ---------------------------------------------------------------------------------------------
/**
 * One member's comp limit, in place of their role's: "owners can decide how much comp, and if no
 * limit — individually for each manager as well". It replaces the role's three comp values
 * (`compUnlimited`, `compMaxMinutesPerAction`, `compMaxMinutesPerShift`) for this member and nobody
 * else, and is then read exactly as a role's are: a number is met with the setting (the lower wins,
 * the setting lifted by `sessions:comp_unlimited`), `null` is the setting alone, and `compUnlimited`
 * is no comp limit at all. When `compUnlimited` is `true`, both numbers are `null`.
 */
export const MemberCompLimitSchema = z
    .object({
    compUnlimited: z.boolean(),
    compMaxMinutesPerAction: minutesLimit,
    compMaxMinutesPerShift: minutesLimit,
})
    .strict()
    .openapi('MemberCompLimit', {
    description: 'A member’s own comp limit, in place of their role’s (spec/07 §6.4). `compUnlimited`: no comp limit at all. ' +
        'A number is met with the setting, the lower winning; `null` is the setting alone.',
});
export const MemberLimitsSchema = z
    .object({
    /** The member's own comp limit, or `null`: their role's applies. */
    comp: MemberCompLimitSchema.nullable(),
})
    .strict()
    .openapi('MemberLimits', {
    description: 'The limits set on one member rather than on their role (spec/07 §6.4). `null`: the role’s apply.',
});
export const RoleSchema = z
    .object({
    name: RoleNameSchema,
    /** One of spec/07 §6.2's roles, defined in code; otherwise a custom role of the organisation. */
    builtIn: z.boolean(),
    /** Every permission the role lists. */
    permissions: z.array(PermissionSchema),
    limits: RoleLimitsSchema,
})
    .openapi('Role', { description: 'An organisation role: its permissions and its limits (spec/07 §6.2–§6.4).' });
export const RoleListSchema = z
    .object({
    /** The built-in roles of spec/07 §6.2 in that table's order, then the custom roles by name. */
    data: z.array(RoleSchema),
})
    .openapi('RoleList', { description: 'Every role of the organisation: the built-in ones and its custom roles.' });
/**
 * A custom role's permissions: at least one, each one of the permissions the server enforces
 * (`ORGANIZATION_PERMISSIONS`). Anything else — a platform permission, or one of spec/07 §6.3's table
 * that no endpoint gates yet — is refused rather than stored, because a stored permission that gates
 * nothing is one somebody believes they granted.
 */
const CustomRolePermissionsSchema = z.array(PermissionSchema).min(1).max(200);
export const CreateRoleSchema = z
    .object({
    /** Lower case letters, digits and underscores; not the name of a built-in role. */
    name: RoleNameSchema,
    permissions: CustomRolePermissionsSchema,
})
    .strict()
    .openapi('CreateRole', { description: 'A new custom role: its name and the permissions it combines.' });
export const UpdateRoleSchema = z
    .object({
    /** The role's whole permission set, replacing the one it has. */
    permissions: CustomRolePermissionsSchema,
})
    .strict()
    .openapi('UpdateRole', { description: 'A custom role’s new permission set.' });
export const INVITATION_CHANNELS = ['phone', 'email'];
export const InvitationChannelSchema = z.enum(INVITATION_CHANNELS).openapi('InvitationChannel');
export const INVITATION_STATUSES = ['pending', 'accepted', 'revoked', 'expired'];
export const InvitationStatusSchema = z.enum(INVITATION_STATUSES).openapi('InvitationStatus');
export const InvitationSchema = z
    .object({
    id: InvitationIdSchema,
    organizationId: OrganizationIdSchema,
    channel: InvitationChannelSchema,
    /** The number or address invited, masked. */
    destination: z.string(),
    role: RoleNameSchema,
    venueIds: VenueScopeSchema,
    status: InvitationStatusSchema,
    expiresAt: IsoDateTimeSchema,
    createdAt: IsoDateTimeSchema,
})
    .openapi('Invitation', { description: 'An invitation to join the staff (spec/07 §6.5).' });
export const InvitationListSchema = paginated(InvitationSchema, 'InvitationList');
export const CreateInvitationSchema = z
    .object({
    /** Exactly one of `phoneNumber` and `email`. */
    phoneNumber: PhoneNumberSchema.optional(),
    email: z.string().trim().min(3).max(254).regex(/^[^\s@]+@[^\s@]+$/, 'An email address').optional(),
    role: RoleNameSchema,
    venueIds: VenueScopeSchema.default([]),
})
    .refine((body) => (body.phoneNumber === undefined) !== (body.email === undefined), {
    message: 'Give a phone number or an email address — exactly one.',
    path: ['phoneNumber'],
})
    .openapi('CreateInvitation');
export const INVITATION_DELIVERY_STATUSES = ['sent', 'not_sent'];
/** The channels an invitation message can travel by (N-25, spec/16 §4.3). */
export const InvitationMessageChannelSchema = z
    .enum(['sms', 'whatsapp', 'email'])
    .openapi('InvitationMessageChannel');
export const InvitationDeliverySchema = z
    .object({
    /**
     * `sent` once a provider has accepted the message — never before. `not_sent` when no provider
     * can carry it in this environment (SMS and WhatsApp are not connected yet, F-CMP-09) or the one
     * that could failed; the invitation still stands, and `acceptUrl` can be passed on another way.
     */
    status: z.enum(INVITATION_DELIVERY_STATUSES),
    /** The channel it went by. Absent when it did not go. */
    channel: InvitationMessageChannelSchema.optional(),
})
    .openapi('InvitationDelivery', { description: 'Whether the invitation message (N-25) was sent.' });
// A fresh object rather than `InvitationSchema.extend(…)`: extending a registered component emits
// `allOf`, which the client generators refuse (tools/src/codegen).
export const CreatedInvitationSchema = z.object({
    ...InvitationSchema.shape,
    /**
     * The link, which only the invited person can use: accepting needs a session signed in with this
     * phone number or this email address, verified. Returned so the inviter can pass it on when no
     * message could be sent.
     */
    acceptUrl: z.string(),
    delivery: InvitationDeliverySchema,
}).openapi('CreatedInvitation', { description: 'An invitation just made, with its link and whether its message went.' });
export const AcceptInvitationSchema = z
    .object({
    /** The token from the invitation link (the part after `#`). */
    token: z.string().min(20).max(512),
})
    .openapi('AcceptInvitation');
/**
 * Who sent an invitation: `hq`, the Platform's own team, inviting a café's first owner (ADR-0046); or
 * `member`, someone on the organisation's staff.
 */
export const INVITATION_SENDERS = ['hq', 'member'];
/** The invited contact in full: an email address or a phone number, the one the invitation names. */
export const InvitationPreviewContactSchema = z
    .object({ email: z.string().optional(), phoneNumber: z.string().optional() })
    .openapi('InvitationPreviewContact', { description: 'The number or address invited, in full.' });
/**
 * What an invitation link is, read with the link alone, before signing in (ADR-0046). **Only while it can
 * be accepted** — `status` is `pending` — does it say anything more than `status`: who invited whom to what,
 * until when, and the invited contact in full (`contact`), for the accept page to create the account with
 * it, prefilled and locked. The token is the invitation, addressed to that one contact. Once it was used,
 * withdrawn or has expired, the preview is `{ status }` and nothing else: a link that no longer works
 * names no organisation, no inviter and no contact to whoever holds it.
 */
export const InvitationPreviewSchema = z
    .object({
    status: InvitationStatusSchema,
    /** The organisation's name. Only while `pending`. */
    organizationName: z.string().optional(),
    /** The role offered. Only while `pending`. */
    role: RoleNameSchema.optional(),
    /** Only while `pending`. */
    invitedBy: z.enum(INVITATION_SENDERS).optional(),
    /** The inviter's name, for a `member` invitation whose account gives one; `null` otherwise. Only while `pending`. */
    inviterName: z.string().nullable().optional(),
    /** Only while `pending`. */
    channel: InvitationChannelSchema.optional(),
    /** The number or address invited, masked. Only while `pending`. */
    destination: z.string().optional(),
    /** The number or address invited, in full. Only while `pending`. */
    contact: InvitationPreviewContactSchema.optional(),
    /** Only while `pending`. */
    expiresAt: IsoDateTimeSchema.optional(),
})
    .openapi('InvitationPreview', {
    description: 'An invitation, as its link reads before signing in: everything while it is pending, its status alone after.',
});
export const AcceptedInvitationSchema = z
    .object({ organizationId: OrganizationIdSchema, member: MemberSchema })
    .openapi('AcceptedInvitation', { description: 'The membership the invitation created.' });
const MemberParamsSchema = OrganizationScopeParamsSchema.extend({ memberId: MemberIdSchema }).openapi('MemberParams');
const RoleParamsSchema = OrganizationScopeParamsSchema.extend({ role: RoleNameSchema }).openapi('RoleParams');
const InvitationParamsSchema = OrganizationScopeParamsSchema.extend({ invitationId: InvitationIdSchema }).openapi('InvitationParams');
const RULE = 'The role must be below your own — its permissions a strict subset of yours — and only an owner may grant, ' +
    'change or remove the owner role (spec/07 §6.3).';
const json = (schema) => ({
    body: { content: { 'application/json': { schema } }, required: true },
});
const notFound = problemResponse('No such organisation or member, or not one you can see.', 'not-found');
export const listMembersRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/members',
    operationId: 'listMembers',
    tags: ['members'],
    summary: 'The organisation’s staff',
    description: 'Every member, oldest first, with their role, venues and whether they have a second factor.',
    [PERMISSION_EXTENSION]: MEMBERS_READ,
    request: { params: OrganizationScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(MemberListSchema, 'A page of members.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getMemberRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/members/{memberId}',
    operationId: 'getMember',
    tags: ['members'],
    summary: 'One member of the staff',
    description: 'Their role, venues, status and whether they have a second factor. A member who works at some venues only ' +
        'sees the members who work at those venues, and no one else.',
    [PERMISSION_EXTENSION]: MEMBERS_READ,
    request: { params: MemberParamsSchema },
    responses: {
        200: jsonResponse(MemberSchema, 'The member.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const changeMemberRoleRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/members/{memberId}/role',
    operationId: 'changeMemberRole',
    tags: ['members'],
    summary: 'Give a member a different role',
    description: `${RULE} The last owner cannot be given another role.`,
    [PERMISSION_EXTENSION]: MEMBERS_ROLES,
    request: { params: MemberParamsSchema, ...json(ChangeMemberRoleSchema) },
    responses: {
        200: jsonResponse(MemberSchema, 'The member, with the new role.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        409: problemResponse('That would leave the organisation without an owner.', 'last-owner'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const changeMemberVenuesRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/members/{memberId}/venues',
    operationId: 'changeMemberVenues',
    tags: ['members'],
    summary: 'Change which venues a member works at',
    description: `An empty list is every venue. ${RULE} An owner works at every venue.`,
    [PERMISSION_EXTENSION]: MEMBERS_UPDATE,
    request: { params: MemberParamsSchema, ...json(ChangeMemberVenuesSchema) },
    responses: {
        200: jsonResponse(MemberSchema, 'The member, with the new venues.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const changeMemberStatusRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/members/{memberId}/status',
    operationId: 'changeMemberStatus',
    tags: ['members'],
    summary: 'Suspend a member, or reinstate one',
    description: `${RULE} Suspending ends their console access on their next request, as removing them would, and keeps their ` +
        'role and venues; reinstating gives back exactly those. Their account, and signing in as a player, are ' +
        'untouched. Nobody changes their own status, and the last active owner cannot be suspended.',
    [PERMISSION_EXTENSION]: MEMBERS_REMOVE,
    request: { params: MemberParamsSchema, ...json(ChangeMemberStatusSchema) },
    responses: {
        200: jsonResponse(MemberSchema, 'The member, with the new status.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        409: problemResponse('That would leave the organisation without an active owner.', 'last-owner'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const removeMemberRoute = createRoute({
    method: 'delete',
    path: '/v1/organizations/{organizationId}/members/{memberId}',
    operationId: 'removeMember',
    tags: ['members'],
    summary: 'Take someone off the staff',
    description: `${RULE} Their console access ends on their next request; their account, and signing in as a player, ` +
        'are untouched. The last owner cannot be removed.',
    [PERMISSION_EXTENSION]: MEMBERS_REMOVE,
    request: { params: MemberParamsSchema },
    responses: {
        204: { description: 'Removed.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        409: problemResponse('That would leave the organisation without an owner.', 'last-owner'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createInvitationRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/invitations',
    operationId: 'createInvitation',
    tags: ['members'],
    summary: 'Invite someone to the staff, by phone or by email',
    description: `${RULE} The link works once, for seven days, and only for someone signed in with that number or address. ` +
        'The message (N-25) is sent when a provider can carry it, and `delivery` says whether it was.',
    [PERMISSION_EXTENSION]: MEMBERS_INVITE,
    request: { params: OrganizationScopeParamsSchema, ...json(CreateInvitationSchema) },
    responses: {
        201: jsonResponse(CreatedInvitationSchema, 'The invitation, and whether its message went.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        409: problemResponse('That person is already on the staff. Or: an invitation to a phone number while phone sign-in is turned off on the Platform (`auth.phoneSignIn`) — email only; nothing was written or sent.', 'already-a-member', 'phone-invitations-off'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listInvitationsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/invitations',
    operationId: 'listInvitations',
    tags: ['members'],
    summary: 'Invitations sent, newest first',
    description: 'Pending, accepted, revoked and expired. Never the link itself.',
    [PERMISSION_EXTENSION]: MEMBERS_READ,
    request: { params: OrganizationScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(InvitationListSchema, 'A page of invitations.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const revokeInvitationRoute = createRoute({
    method: 'delete',
    path: '/v1/organizations/{organizationId}/invitations/{invitationId}',
    operationId: 'revokeInvitation',
    tags: ['members'],
    summary: 'Withdraw an invitation',
    description: `The link stops working at once. ${RULE}`,
    [PERMISSION_EXTENSION]: MEMBERS_INVITE,
    request: { params: InvitationParamsSchema },
    responses: {
        204: { description: 'Withdrawn.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such invitation in this organisation.', 'not-found'),
        409: problemResponse('The invitation was already used, withdrawn or expired.', 'invitation-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const roleNotFound = problemResponse('No such role in this organisation.', 'not-found');
export const getRoleRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/roles/{role}',
    operationId: 'getRole',
    tags: ['members'],
    summary: 'One role: its permissions and its limits',
    description: 'A built-in role of spec/07 §6.2 or a custom role of the organisation.',
    [PERMISSION_EXTENSION]: MEMBERS_READ,
    request: { params: RoleParamsSchema },
    responses: {
        200: jsonResponse(RoleSchema, 'The role.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: roleNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const changeRoleLimitsRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/roles/{role}/limits',
    operationId: 'changeRoleLimits',
    tags: ['members'],
    summary: 'Set the limits a role carries',
    description: `${RULE} Every limit is given, \`null\` for none of the role’s own. Nobody gives a limit higher than their own ` +
        'role’s, and because a role’s limits reach its holders at every venue, only someone who works at every venue ' +
        'sets them. The owner role carries none. Amounts are in the organisation’s currency.',
    [PERMISSION_EXTENSION]: MEMBERS_ROLES,
    request: { params: RoleParamsSchema, ...json(RoleLimitsSchema) },
    responses: {
        200: jsonResponse(RoleSchema, 'The role, with its new limits.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: roleNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getMemberLimitsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/members/{memberId}/limits',
    operationId: 'getMemberLimits',
    tags: ['members'],
    summary: 'The limits set on one member',
    description: 'The member’s own comp limit, which takes the place of their role’s, or `null` when their role’s applies ' +
        '(spec/07 §6.4). A member who works at some venues only sees the members who work at those venues.',
    [PERMISSION_EXTENSION]: MEMBERS_READ,
    request: { params: MemberParamsSchema },
    responses: {
        200: jsonResponse(MemberLimitsSchema, 'The member’s own limits.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const changeMemberLimitsRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/members/{memberId}/limits',
    operationId: 'changeMemberLimits',
    tags: ['members'],
    summary: 'Set the limits on one member',
    description: `${RULE} Sets the member’s own comp limit in place of their role’s, or \`comp: null\` to go back to the ` +
        'role’s. Nobody gives a limit higher than their own — `null` (the setting alone) is higher than any number, ' +
        'and `compUnlimited` higher still, so only an owner or someone whose own comp is unlimited gives it. Nobody ' +
        'sets their own, and an owner carries none. Audited.',
    [PERMISSION_EXTENSION]: MEMBERS_ROLES,
    request: { params: MemberParamsSchema, ...json(MemberLimitsSchema) },
    responses: {
        200: jsonResponse(MemberLimitsSchema, 'The member’s own limits, as now set.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const CUSTOM_RULE = 'Only permissions you hold yourself, and the role must stay below your own — an owner is not bound by that. A ' +
    'custom role reaches its holders at every venue, so only someone who works at every venue makes, changes or ' +
    'deletes one (spec/07 §6.2–§6.3).';
export const listRolesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/roles',
    operationId: 'listRoles',
    tags: ['members'],
    summary: 'Every role: the built-in ones and the custom ones',
    description: 'Each with its permissions and limits — what the Roles screen renders. The built-in roles’ grants are the ' +
        'table in `@clavian/contracts`, so the screen and the server read one list.',
    [PERMISSION_EXTENSION]: MEMBERS_READ,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(RoleListSchema, 'The roles.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createRoleRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/roles',
    operationId: 'createRole',
    tags: ['members'],
    summary: 'Make a custom role',
    description: `${CUSTOM_RULE} At most ${CUSTOM_ROLES_MAX} custom roles per organisation. It starts with no limits of its own.`,
    [PERMISSION_EXTENSION]: MEMBERS_ROLES,
    request: { params: OrganizationScopeParamsSchema, ...json(CreateRoleSchema) },
    responses: {
        201: jsonResponse(RoleSchema, 'The new role.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        409: problemResponse('A role by that name exists, or the organisation already has as many custom roles as it may.', 'role-name-taken', 'custom-role-limit-reached'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateRoleRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/roles/{role}',
    operationId: 'updateRole',
    tags: ['members'],
    summary: 'Change a custom role’s permissions',
    description: `${CUSTOM_RULE} Both the role as it is and as it will be must be below your own. Its holders’ consoles are ` +
        'told at once (`access.changed`), and their next request is answered with the new set. A built-in role is ' +
        'defined in code and cannot be changed.',
    [PERMISSION_EXTENSION]: MEMBERS_ROLES,
    request: { params: RoleParamsSchema, ...json(UpdateRoleSchema) },
    responses: {
        200: jsonResponse(RoleSchema, 'The role, with its new permissions.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: roleNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const deleteRoleRoute = createRoute({
    method: 'delete',
    path: '/v1/organizations/{organizationId}/roles/{role}',
    operationId: 'deleteRole',
    tags: ['members'],
    summary: 'Delete a custom role',
    description: `${CUSTOM_RULE} Refused while any member holds it — a suspended one included — or a pending invitation names ` +
        'it: give them another role first. Its limits go with it.',
    [PERMISSION_EXTENSION]: MEMBERS_ROLES,
    request: { params: RoleParamsSchema },
    responses: {
        204: { description: 'Deleted.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: roleNotFound,
        409: problemResponse('A member holds the role, or a pending invitation names it.', 'role-in-use'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const acceptInvitationRoute = createRoute({
    method: 'post',
    path: '/v1/invitations/accept',
    operationId: 'acceptInvitation',
    tags: ['members'],
    summary: 'Join the staff with an invitation link',
    description: 'Needs a session signed in with the invited phone number or email address, verified. When the role ' +
        'needs a second factor (`staff.require2faRoles`), the session must have completed one — set up an ' +
        'authenticator first. Adds this one membership and no other.',
    request: json(AcceptInvitationSchema),
    responses: {
        200: jsonResponse(AcceptedInvitationSchema, 'The new membership.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Signed in as someone other than the person invited, or the role needs a second factor first.', 'invitation-contact-mismatch', 'second-factor-required'),
        404: problemResponse('No such invitation.', 'not-found'),
        409: problemResponse('The invitation was used, withdrawn or has expired, or you are already on the staff.', 'invitation-unavailable', 'already-a-member'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const previewInvitationRoute = createRoute({
    method: 'post',
    path: '/v1/invitations/preview',
    operationId: 'previewInvitation',
    tags: ['members'],
    summary: 'Read an invitation link before signing in',
    description: 'No session needed: the signed token is the credential, sent in the body so it never reaches a log. While the ' +
        'invitation can be accepted (`pending`), says which organisation invited whom, to which role, by whom, until when, ' +
        'and the invited email address or phone number in full, so the account is created with it (ADR-0046). Once it was ' +
        'used, withdrawn or has expired, the answer is its `status` alone. A token that is not one the server signed is ' +
        '`404`, exactly as an unknown invitation is. Served `no-store`. Changes nothing.',
    request: json(AcceptInvitationSchema),
    responses: {
        200: jsonResponse(InvitationPreviewSchema, 'The invitation.'),
        400: commonProblemResponses[400],
        404: problemResponse('No such invitation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const MEMBER_ROUTES = [
    listMembersRoute,
    getMemberRoute,
    changeMemberRoleRoute,
    changeMemberVenuesRoute,
    changeMemberStatusRoute,
    removeMemberRoute,
    getRoleRoute,
    changeRoleLimitsRoute,
    getMemberLimitsRoute,
    changeMemberLimitsRoute,
    listRolesRoute,
    createRoleRoute,
    updateRoleRoute,
    deleteRoleRoute,
    createInvitationRoute,
    listInvitationsRoute,
    revokeInvitationRoute,
    acceptInvitationRoute,
    previewInvitationRoute,
];
//# sourceMappingURL=members.js.map